import { Input, Link } from '@pinpointhq/thumbtack';
import * as React from 'react';
import { shownValue } from '../../utilities/shownValue';
import { EditableRow } from './EditableRow';

export interface IEditableTextFieldProps {
  canEdit: boolean;
  canMask?: boolean;
  defaultValue: string;
  isLink?: boolean;
  name: string;
  style?: React.CSSProperties;
  handleSave(value: string): Promise<boolean>;
}

export function EditableTextField({
  canEdit,
  canMask,
  defaultValue,
  isLink = false,
  name,
  style,
  ...props
}: IEditableTextFieldProps) {
  const [value, setValue] = React.useState<string>(defaultValue);
  const [isEditing, setIsEditing] = React.useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const inputRef = React.createRef<HTMLInputElement>();
  const [isMasked, setIsMasked] = React.useState<boolean>(true);

  React.useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  function handleCancel() {
    setValue(defaultValue);
    setIsEditing(false);
    if (canMask) {
      setIsMasked(true);
    }
  }

  function handleEdit() {
    setIsEditing(true);
    if (canMask) {
      setValue('');
    }
  }

  function handleSave() {
    setIsSubmitting(true);
    props.handleSave(value).then((success) => {
      setIsSubmitting(false);

      if (success) {
        setIsEditing(false);
      }
    });
  }

  React.useEffect(() => {
    if (isEditing) inputRef.current.focus();
  }, [isEditing]);

  if (!canEdit) {
    return (
      <span>
        {isLink ? (
          <Link target="_blank" href={value}>
            {value}
          </Link>
        ) : (
          value
        )}
      </span>
    );
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      handleSave();
    }
  }

  const handleMask = () => setIsMasked((prevMask) => !prevMask);
  const handleInput = (event: React.KeyboardEvent<HTMLInputElement>) => setValue(event.currentTarget.value);

  const editingInput = (
    <Input
      {...{ name, style, value }}
      type="text"
      onChange={handleInput}
      onKeyDown={handleKeyDown}
      ref={inputRef}
      disabled={isSubmitting}
    />
  );

  return (
    <EditableRow
      canMask={canMask}
      isEditing={isEditing}
      isMasked={isMasked}
      showEdit={canEdit}
      handleCancel={handleCancel}
      handleEdit={handleEdit}
      handleMask={handleMask}
      handleSave={handleSave}
      isSubmitting={isSubmitting}
      editingInput={editingInput}
    >
      {shownValue({ canMask, isMasked, defaultValue, isLink, value })}
    </EditableRow>
  );
}
